import React, { useEffect, useState } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import { url } from '../../util/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const getFirstDayOfMonth = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
};
const getToday = () => new Date().toISOString().slice(0, 10);

const STATUS_COLORS = {
    delivered:  { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
    read:       { bg: '#f5f3ff', color: '#7c3aed', border: '#ddd6fe' },
    submitted:  { bg: '#eff6ff', color: '#1565c0', border: '#bfdbfe' },
    failed:     { bg: '#fef2f2', color: '#dc2626', border: '#fecaca' },
    pending:    { bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
};

const getStatusStyle = (status = '') => {
    return STATUS_COLORS[status.toLowerCase()] || { bg: '#f3f4f6', color: '#6b7280', border: '#e5e7eb' };
};

const DetailedReportComponent = () => {
    const [users, setUsers] = useState([]);
    const [senders, setSenders] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedSender, setSelectedSender] = useState('');
    const [startDate, setStartDate] = useState(getFirstDayOfMonth());
    const [endDate, setEndDate] = useState(getToday());
    const [reports, setReports] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isSendersLoading, setIsSendersLoading] = useState(false);
    const [statusOverlay, setStatusOverlay] = useState({ open: false, statuses: [] });

    // Fetch users
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${url}/users`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error();
                const data = await response.json();
                setUsers(data || []);
            } catch { toast.error('Failed to load users'); }
        };
        fetchUsers();
    }, []);

    // Fetch senders filtered by selected user
    useEffect(() => {
        const fetchSenders = async () => {
            setIsSendersLoading(true);
            setSelectedSender('');
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${url}/profiles`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!response.ok) throw new Error();
                const data = await response.json();
                const filtered = selectedUser
                    ? data.filter(p => p.owner === selectedUser || p.owner?._id === selectedUser)
                    : data;
                setSenders(filtered);
            } catch { setSenders([]); }
            finally { setIsSendersLoading(false); }
        };
        fetchSenders();
    }, [selectedUser]);

    // Fetch reports — FIX: ${query} not $(query)
    const fetchReports = async (page = 1) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const skip = (page - 1) * itemsPerPage;
            const query = `limit=${itemsPerPage}&skip=${skip}`;
            const body = { startdate: startDate, enddate: endDate };
            if (selectedUser) body.userid = selectedUser;
            if (selectedSender) body.sender = selectedSender;

            const response = await fetch(`${url}/detailedReport?${query}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(body),
            });
            if (!response.ok) throw new Error();
            const data = await response.json();
            setReports(data.data || []);
            setTotalCount(data.total || 0);
        } catch { toast.error('Failed to load report data'); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchReports(1); /* eslint-disable-next-line */ }, []);

    const handleSearch = () => { setCurrentPage(1); fetchReports(1); };
    const handlePageChange = (page) => { setCurrentPage(page); fetchReports(page); };

    const getLatestStatus = (statusArr) => {
        if (!Array.isArray(statusArr) || statusArr.length === 0) return null;
        return [...statusArr].sort((a, b) => new Date(b.time) - new Date(a.time))[0];
    };

    const handleStatusClick = (e, statusArr) => {
        e.stopPropagation();
        setStatusOverlay({
            open: true,
            statuses: [...statusArr].sort((a, b) => new Date(b.time || b.datetime) - new Date(a.time || a.datetime)),
        });
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Detailed Report</h5>
                        <p style={styles.pageSubtitle}>Message-level delivery details</p>
                    </div>
                </div>

                {/* Filters */}
                <div style={styles.filterCard}>
                    <div style={styles.filterGrid}>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>User</label>
                            <select style={styles.filterSelect} value={selectedUser}
                                onChange={e => { setSelectedUser(e.target.value); setSelectedSender(''); }}>
                                <option value="">All Users</option>
                                {users.map(user => (
                                    <option key={user._id} value={user._id}>{user.name}</option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Sender</label>
                            <select style={styles.filterSelect} value={selectedSender}
                                onChange={e => setSelectedSender(e.target.value)}
                                disabled={!selectedUser || isSendersLoading}>
                                <option value="">{isSendersLoading ? 'Loading…' : 'All Senders'}</option>
                                {senders.map((s, i) => (
                                    <option key={i} value={s.whatsapp_no}>{s.whatsapp_no}</option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>Start Date</label>
                            <input type="date" style={styles.filterInput} value={startDate}
                                max={endDate} onChange={e => setStartDate(e.target.value)} />
                        </div>
                        <div style={styles.filterGroup}>
                            <label style={styles.filterLabel}>End Date</label>
                            <input type="date" style={styles.filterInput} value={endDate}
                                min={startDate} max={getToday()} onChange={e => setEndDate(e.target.value)} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <button style={styles.btnPrimary} onClick={handleSearch} disabled={loading}>
                                {loading
                                    ? <><span style={styles.spinner}></span> Loading…</>
                                    : <><i className="bi bi-search" style={{ marginRight: 6 }}></i>Search</>}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Table card */}
                <div style={styles.tableCard}>
                    <div style={styles.tableHeader}>
                        <h6 style={styles.tableTitle}>
                            <i className="bi bi-list-ul" style={{ marginRight: 8, color: '#1565c0' }}></i>
                            Results
                        </h6>
                        <span style={styles.resultCount}>{totalCount.toLocaleString()} record{totalCount !== 1 ? 's' : ''}</span>
                    </div>

                    {loading ? (
                        <div style={styles.centerState}>
                            <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                            <p style={styles.stateText}>Loading report data…</p>
                        </div>
                    ) : reports.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-file-earmark-bar-graph" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>No records found for the selected filters.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.tableHead}>
                                        <th style={styles.th}>Sender</th>
                                        <th style={styles.th}>Receiver</th>
                                        <th style={styles.th}>Campaign</th>
                                        <th style={styles.th}>Balance Used</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((row, idx) => {
                                        const latest = getLatestStatus(row.status);
                                        const statusStyle = latest ? getStatusStyle(latest.status) : null;
                                        return (
                                            <tr key={idx} style={styles.tr}>
                                                <td style={styles.td}>{row.sender}</td>
                                                <td style={styles.td}>{row.receiver}</td>
                                                <td style={styles.td}>{row.campaign_name || '—'}</td>
                                                <td style={styles.td}>{row.balanceused ?? '—'}</td>
                                                <td style={styles.td}>
                                                    {Array.isArray(row.status) && row.status.length > 0 && latest ? (
                                                        <span
                                                            style={{ ...styles.statusBadge, background: statusStyle.bg, color: statusStyle.color, border: `1px solid ${statusStyle.border}`, cursor: 'pointer' }}
                                                            onClick={e => handleStatusClick(e, row.status)}
                                                        >
                                                            {latest.status}
                                                            <i className="bi bi-chevron-right" style={{ fontSize: 10, marginLeft: 4 }}></i>
                                                        </span>
                                                    ) : <span style={styles.naText}>—</span>}
                                                </td>
                                                <td style={styles.td}>
                                                    {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {!loading && totalCount > 0 && (
                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={Math.ceil(totalCount / itemsPerPage)}
                        onPageChange={handlePageChange}
                    />
                )}

                <ToastContainer />

                {/* Status History Modal */}
                {statusOverlay.open && (
                    <div style={styles.modalOverlay} onClick={() => setStatusOverlay({ open: false, statuses: [] })}>
                        <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
                            <div style={styles.modalHeader}>
                                <div style={styles.modalIcon}>
                                    <i className="bi bi-clock-history" style={{ fontSize: 15, color: '#1565c0' }}></i>
                                </div>
                                <div>
                                    <h6 style={styles.modalTitle}>Status History</h6>
                                    <p style={styles.modalSubtitle}>{statusOverlay.statuses.length} status update{statusOverlay.statuses.length !== 1 ? 's' : ''}</p>
                                </div>
                                <button style={styles.closeBtn} onClick={() => setStatusOverlay({ open: false, statuses: [] })}>
                                    <i className="bi bi-x-lg"></i>
                                </button>
                            </div>
                            <div style={styles.modalBody}>
                                <div style={styles.timeline}>
                                    {statusOverlay.statuses.map((s, i) => {
                                        const sc = getStatusStyle(s.status);
                                        return (
                                            <div key={i} style={styles.timelineItem}>
                                                <div style={styles.timelineLeft}>
                                                    <div style={{ ...styles.timelineDot, background: sc.color }}></div>
                                                    {i < statusOverlay.statuses.length - 1 && <div style={styles.timelineLine}></div>}
                                                </div>
                                                <div style={{ ...styles.timelineCard, background: sc.bg, border: `1px solid ${sc.border}` }}>
                                                    <span style={{ ...styles.timelineStatus, color: sc.color }}>{s.status}</span>
                                                    <span style={styles.timelineTime}>
                                                        {s.time || s.datetime ? new Date(s.time || s.datetime).toLocaleString() : '—'}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    filterCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '16px 20px', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    filterGrid: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' },
    filterGroup: { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 140 },
    filterLabel: { fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' },
    filterSelect: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151', background: 'white' },
    filterInput: { padding: '8px 12px', fontSize: '13px', border: '1.5px solid #e5e7eb', borderRadius: '8px', outline: 'none', color: '#374151' },
    tableCard: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    tableHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f3f4f6' },
    tableTitle: { margin: 0, fontSize: '14px', fontWeight: '700', color: '#111827', display: 'flex', alignItems: 'center' },
    resultCount: { fontSize: '12px', color: '#6b7280', background: '#f3f4f6', padding: '3px 10px', borderRadius: '20px' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    tableHead: { background: '#eff6ff' },
    th: { padding: '12px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#1565c0', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid #bfdbfe', whiteSpace: 'nowrap' },
    tr: { borderBottom: '1px solid #f3f4f6' },
    td: { padding: '11px 14px', color: '#374151', verticalAlign: 'middle' },
    statusBadge: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
    naText: { color: '#9ca3af' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
    spinner: { width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite', marginRight: 6 },
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
    modalBox: { background: 'white', borderRadius: '12px', width: '100%', maxWidth: '420px', maxHeight: '80vh', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    modalHeader: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc', flexShrink: 0 },
    modalIcon: { width: '38px', height: '38px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    modalTitle: { margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' },
    modalSubtitle: { margin: '2px 0 0', fontSize: '12px', color: '#6b7280' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '13px', padding: 0, marginLeft: 'auto' },
    modalBody: { padding: '16px 20px', overflowY: 'auto', flex: 1 },
    timeline: { display: 'flex', flexDirection: 'column', gap: 0 },
    timelineItem: { display: 'flex', gap: 12, alignItems: 'flex-start' },
    timelineLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, paddingTop: 4 },
    timelineDot: { width: 12, height: 12, borderRadius: '50%', flexShrink: 0 },
    timelineLine: { width: 2, flex: 1, background: '#e5e7eb', minHeight: 24, marginTop: 4 },
    timelineCard: { flex: 1, borderRadius: '8px', padding: '10px 14px', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
    timelineStatus: { fontSize: '13px', fontWeight: '700' },
    timelineTime: { fontSize: '12px', color: '#6b7280' },
};

export default DetailedReportComponent;
